import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  TrendingUp, Clock, DollarSign, Zap, Award, Target,
  ArrowUpRight, RefreshCw, Info, Layers, Download,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtNum = (n) => new Intl.NumberFormat('en-US').format(Math.round(n || 0));
const fmtUSD = (n) => `$${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(n || 0))}`;
const fmtUSDCompact = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${Math.round(n)}`;

// Animated counter — 800ms count-up
function useCounter(target, duration = 800) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf, start;
    const from = 0;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setVal(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function KPICard({ icon: Icon, iconClass, label, value, delta, subLabel, testId }) {
  const animated = useCounter(typeof value === 'number' ? value : 0);
  const display = typeof value === 'number' ? fmtNum(animated) : value;
  return (
    <Card className="relative overflow-hidden" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`p-2 rounded-lg ${iconClass}`}>
            <Icon className="h-4 w-4" />
          </div>
          {delta && (
            <Badge variant="outline" className="text-[10px] gap-1 text-emerald-600 border-emerald-200 bg-emerald-50">
              <ArrowUpRight className="h-3 w-3" /> {delta}
            </Badge>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold tracking-tight text-text-primary">{display}</p>
          <p className="text-xs text-gray-medium mt-0.5">{label}</p>
          {subLabel && <p className="text-[10px] text-gray-400 mt-0.5 italic">{subLabel}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function EfficiencyROIPage() {
  const { user, effectiveRole } = useAuth();
  const role = effectiveRole || user?.role;
  const [hourlyRate, setHourlyRate] = useState(35);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(role === 'team_member' ? 'employee' : 'subscriber');
  const [pdfBusy, setPdfBusy] = useState(false);

  // Which endpoints does this role see?
  const scopes = useMemo(() => {
    if (role === 'super_admin') return [
      { id: 'subscriber', label: 'As Subscriber' },
      { id: 'operator', label: 'As Operator' },
      { id: 'platform', label: 'Platform-wide' },
    ];
    if (role === 'country_operator') return [
      { id: 'subscriber', label: 'As Subscriber' },
      { id: 'operator', label: 'My Subscribers' },
    ];
    if (role === 'team_member') return [
      { id: 'employee', label: 'My Personal ROI' },
    ];
    return [{ id: 'subscriber', label: 'My Workspace' }];
  }, [role]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/roi/${scope}?hourly_rate=${hourlyRate}`);
      setData(r.data);
    } catch (e) {
      console.error('ROI fetch failed', e);
    } finally { setLoading(false); }
  }, [scope, hourlyRate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Investor-Snapshot PDF export — reuses the same ROI data pipeline so the
  // number on-screen and the number in the PDF always match.
  const downloadSnapshot = useCallback(async () => {
    setPdfBusy(true);
    try {
      const token = localStorage.getItem('konnect_access_token');
      const url = `${API}/api/roi/snapshot.pdf?scope=${scope}&hourly_rate=${hourlyRate}`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Snapshot failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `konnect-roi-snapshot-${scope}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      console.error('Snapshot PDF failed', e);
    } finally { setPdfBusy(false); }
  }, [scope, hourlyRate]);

  const impactChartData = useMemo(() => {
    if (!data?.per_module) return [];
    return data.per_module.slice(0, 10).map(m => ({
      name: m.label.length > 25 ? m.label.slice(0, 22) + '…' : m.label,
      Manual: Math.round(m.manual_min * m.usage_count / 60),
      Automated: Math.round(m.auto_min * m.usage_count / 60),
      saved: m.hours_saved,
    }));
  }, [data]);

  const categoryData = useMemo(() => {
    if (!data?.by_category) return [];
    return Object.entries(data.by_category)
      .map(([k, v]) => ({ id: k, name: v.label, value: v.hours_saved, color: v.color, usd: v.value_usd }))
      .filter(c => c.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const projectionData = useMemo(() => {
    if (!data?.projection) return [];
    const p = data.projection;
    return [
      { period: 'Month 1', value: p.month_1 },
      { period: 'Month 3', value: p.month_3 },
      { period: 'Month 6', value: p.month_6 },
      { period: 'Month 12', value: p.month_12 },
    ];
  }, [data]);

  const reallocationData = useMemo(() => {
    if (!data?.reallocation) return [];
    return data.reallocation.map(r => ({ name: r.label, value: r.hours, color: r.color, pct: r.pct }));
  }, [data]);

  if (loading || !data) {
    return (
      <div className="page-shell space-y-4" data-testid="roi-page-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const scopeLabel = scopes.find(s => s.id === scope)?.label || 'My Workspace';
  const isPlatform = scope === 'platform';
  const isOperator = scope === 'operator';

  return (
    <div className="page-shell space-y-5" data-testid="roi-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] text-gray-medium">
            <span>Analytics</span> <span>›</span> <span>Efficiency & ROI</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mt-1" data-testid="roi-page-title">
            Efficiency & ROI
          </h1>
          <div className="flex items-center gap-2 mt-1.5">
            <Badge className={data.mode === 'live' ? 'bg-emerald-500' : 'bg-amber-500'} data-testid="roi-mode-badge">
              {data.mode === 'live' ? '● LIVE DATA' : data.mode === 'aggregate' ? '◈ AGGREGATE' : data.mode === 'platform' ? '◆ PLATFORM' : '◎ DEMO DATA'}
            </Badge>
            <span className="text-xs text-gray-medium">
              {isPlatform ? `${data.subscriber_count} subscribers · platform-wide` :
               isOperator ? `${data.subscriber_count} subscribers in your book` :
               `${data.team_size} team members · trailing 30 days`}
              {data.automation_runs_30d > 0 && (
                <span className="ml-2 text-emerald-700 font-medium" data-testid="roi-automation-signal">
                  · <Zap className="inline h-3 w-3" /> {fmtNum(data.automation_runs_30d)} automation runs feeding this
                </span>
              )}
              {data.module_events_30d > 0 && (
                <span className="ml-2 text-sky-700 font-medium" data-testid="roi-cross-module-signal">
                  · <Zap className="inline h-3 w-3" /> {fmtNum(data.module_events_30d)} cross-module events (+{data.module_event_hours_saved}h saved)
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {scopes.length > 1 && (
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" data-testid="roi-scope-toggle">
              {scopes.map(s => (
                <button
                  key={s.id}
                  onClick={() => setScope(s.id)}
                  data-testid={`roi-scope-${s.id}`}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    scope === s.id ? 'bg-brand-primary text-white' : 'text-gray-medium hover:text-text-primary'
                  }`}
                >{s.label}</button>
              ))}
            </div>
          )}
          <div className="inline-flex items-center gap-1.5 border border-gray-200 rounded-lg px-2 py-1 bg-white">
            <span className="text-xs text-gray-medium">$/hr</span>
            <Input
              type="number" min="1" max="1000"
              data-testid="roi-hourly-rate-input"
              className="h-7 w-16 text-xs border-0 shadow-none focus-visible:ring-0 p-0 text-center"
              value={hourlyRate}
              onChange={e => setHourlyRate(parseFloat(e.target.value) || 35)}
            />
          </div>
          <Button
            variant="outline" size="sm" onClick={fetchData} className="h-8 text-xs gap-1.5"
            data-testid="roi-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button
            variant="default" size="sm"
            onClick={downloadSnapshot}
            disabled={pdfBusy}
            className="h-8 text-xs gap-1.5 bg-brand-primary hover:bg-brand-primary/90"
            data-testid="roi-download-snapshot-btn"
          >
            <Download className="h-3.5 w-3.5" /> {pdfBusy ? 'Preparing…' : 'Investor Snapshot (PDF)'}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard
          icon={Clock} iconClass="bg-teal-100 text-teal-700"
          label="Total Hours Saved" value={data.total_hours_saved}
          delta={`+${Math.round(data.total_hours_saved * 0.08)} vs prior`}
          testId="roi-kpi-hours"
        />
        <KPICard
          icon={DollarSign} iconClass="bg-amber-100 text-amber-700"
          label="Productivity Value" value={fmtUSD(data.total_value_usd)}
          subLabel={`Based on $${hourlyRate}/hr — adjust above`}
          testId="roi-kpi-value"
        />
        <KPICard
          icon={Zap} iconClass="bg-orange-100 text-orange-700"
          label="Tasks Automated" value={data.total_tasks_automated}
          delta={`${data.avg_reduction_pct}% time reduction`}
          testId="roi-kpi-tasks"
        />
        <KPICard
          icon={Layers} iconClass="bg-blue-100 text-blue-700"
          label="Modules Active" value={`${data.total_modules_active} / ${data.modules_total}`}
          subLabel={`ROI Score: ${Math.round(data.total_modules_active / data.modules_total * 100)}`}
          testId="roi-kpi-modules"
        />
      </div>

      {/* Personal Rank badge (employee scope) */}
      {scope === 'employee' && data.personal_rank && (
        <Card className={`border-2 ${
          data.personal_rank.tier === 'gold' ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300' :
          data.personal_rank.tier === 'silver' ? 'bg-gradient-to-r from-slate-50 to-gray-50 border-slate-300' :
          data.personal_rank.tier === 'bronze' ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300' :
          'bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-300'
        }`} data-testid="roi-personal-rank">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Award className={`h-12 w-12 ${
                data.personal_rank.tier === 'gold' ? 'text-amber-500' :
                data.personal_rank.tier === 'silver' ? 'text-slate-400' :
                data.personal_rank.tier === 'bronze' ? 'text-orange-500' :
                'text-teal-500'
              }`} />
              <div>
                <p className="text-[10px] uppercase tracking-wide text-gray-medium font-semibold">Your rank</p>
                <p className="text-xl font-bold text-text-primary">{data.personal_rank.label}</p>
                <p className="text-xs text-gray-medium">
                  {data.employee_name} · {data.total_hours_saved} hrs saved · {fmtUSD(data.total_value_usd)} value delivered
                </p>
              </div>
            </div>
            {data.personal_rank.next_at && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wide text-gray-medium">Next rank at</p>
                <p className="text-lg font-bold text-teal-700">{data.personal_rank.next_at} hrs</p>
                <div className="w-32 mt-1">
                  <Progress value={Math.min(100, (data.total_hours_saved / data.personal_rank.next_at) * 100)} className="h-2" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Operator / Platform special metric */}
      {(isOperator || isPlatform) && (
        <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200" data-testid="roi-investor-callout">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-teal-700 font-semibold uppercase tracking-wide">
                {isPlatform ? 'Platform Value Multiplier' : 'Investor View'}
              </p>
              <p className="text-2xl font-bold text-teal-900 mt-1" data-testid="roi-multiplier-value">
                {isPlatform ? `${data.value_multiplier}× ARR` : fmtUSD(data.operator_arr_estimate) + ' ARR'}
              </p>
              <p className="text-xs text-teal-700 mt-1">
                {isPlatform
                  ? `Platform delivers ${data.value_multiplier}× the value customers pay in subscription fees.`
                  : `Annual recurring revenue from your ${data.subscriber_count} subscribers.`}
              </p>
            </div>
            <Award className="h-12 w-12 text-teal-600/40" />
          </CardContent>
        </Card>
      )}

      {/* Automation Impact — top 10 modules by value */}
      <Card data-testid="roi-impact-chart">
        <CardHeader>
          <CardTitle className="text-base">Automation Impact — Top 10 Modules by Value</CardTitle>
          <p className="text-xs text-gray-medium">Manual vs automated hours per month, per module. Sorted by dollar value saved.</p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={impactChartData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 130 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
              <XAxis type="number" tick={{ fontSize: 11 }} label={{ value: 'Hours / month', position: 'insideBottom', offset: -5, style: { fontSize: 10 } }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={125} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderTop: '2px solid #01696F', borderRadius: 8, fontSize: 12 }}
                itemStyle={{ color: '#fff' }} labelStyle={{ color: '#9ca3af' }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Manual" fill="#FDAB43" radius={[0, 4, 4, 0]} />
              <Bar dataKey="Automated" fill="#4F98A3" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
            <div className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-center text-xs">
              <Zap className="inline h-3.5 w-3.5 text-teal-600 mr-1" />
              Avg. reduction: <b>{data.avg_reduction_pct}%</b>
            </div>
            <div className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-center text-xs">
              <Clock className="inline h-3.5 w-3.5 text-amber-600 mr-1" />
              Per-person hours saved: <b>{data.team_size ? Math.round(data.total_hours_saved / data.team_size) : 0} hrs</b>
            </div>
            <div className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-center text-xs">
              <DollarSign className="inline h-3.5 w-3.5 text-emerald-600 mr-1" />
              Per-person value: <b>{fmtUSD(data.team_size ? data.total_value_usd / data.team_size : 0)}</b>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-column: Reallocation Donut + Growth Projection */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3" data-testid="roi-reallocation-chart">
          <CardHeader>
            <CardTitle className="text-base">Time Saved = Value Created</CardTitle>
            <p className="text-xs text-gray-medium">How your team can redirect recovered hours.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie
                    data={reallocationData} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}
                  >
                    {reallocationData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, fontSize: 12 }}
                    itemStyle={{ color: '#fff' }}
                    formatter={(v) => `${v} hrs`}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                <div className="text-center pb-2 border-b border-gray-100">
                  <p className="text-3xl font-bold text-teal-700">{fmtNum(data.total_hours_saved)}</p>
                  <p className="text-[10px] text-gray-medium uppercase tracking-wide">total hrs recovered</p>
                </div>
                {reallocationData.map(r => (
                  <div key={r.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: r.color }} />
                      <span className="text-gray-700">{r.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-mono font-medium">{Math.round(r.value)} hrs</span>
                      <span className="text-gray-400 ml-1">({fmtUSDCompact(r.value * hourlyRate)})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2" data-testid="roi-projection-chart">
          <CardHeader>
            <CardTitle className="text-base">Compound Growth Projection</CardTitle>
            <p className="text-xs text-gray-medium">Value grows as your team learns & activates more modules.</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={projectionData} margin={{ top: 15, right: 5, bottom: 5, left: 0 }}>
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={fmtUSDCompact} tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, fontSize: 12 }}
                  itemStyle={{ color: '#fff' }} formatter={(v) => fmtUSD(v)}
                />
                <Bar dataKey="value" fill="#01696F" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 p-3 rounded-lg bg-teal-50 border-t-2 border-teal-600" data-testid="roi-multiplier-callout">
              <p className="text-[10px] text-teal-700 uppercase tracking-wide font-semibold">ROI multiplier (12-mo)</p>
              <p className="text-xl font-bold text-teal-900 mt-1">
                For every $1 invested in Konnect, you generate <span className="text-teal-600">${data.projection.roi_multiplier}</span> back
              </p>
              <p className="text-[10px] text-teal-700 mt-0.5">
                Annual plan cost: {fmtUSD(data.projection.annual_plan_cost_usd)} · 12-mo value: {fmtUSD(data.projection.month_12)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Module leaderboard */}
      <Card data-testid="roi-module-leaderboard">
        <CardHeader>
          <CardTitle className="text-base">Top 20 Value-Generating Modules</CardTitle>
          <p className="text-xs text-gray-medium">
            Of {data.modules_total} total modules · sorted by dollar value delivered this period
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-medium">
                  <th className="py-2 font-medium">#</th>
                  <th className="py-2 font-medium">Module</th>
                  <th className="py-2 font-medium">Category</th>
                  <th className="py-2 font-medium text-right">Usage</th>
                  <th className="py-2 font-medium text-right">Manual → Auto</th>
                  <th className="py-2 font-medium text-right">Reduction</th>
                  <th className="py-2 font-medium text-right">Hours saved</th>
                  <th className="py-2 font-medium text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {data.per_module.slice(0, 20).map((m, i) => (
                  <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`roi-module-row-${m.id}`}>
                    <td className="py-2 text-gray-400">{i + 1}</td>
                    <td className="py-2 font-medium text-text-primary">{m.label}</td>
                    <td className="py-2">
                      <Badge variant="outline" className="text-[10px]" style={{ borderColor: data.categories[m.category]?.color, color: data.categories[m.category]?.color }}>
                        {data.categories[m.category]?.label}
                      </Badge>
                    </td>
                    <td className="py-2 text-right font-mono">{fmtNum(m.usage_count)}</td>
                    <td className="py-2 text-right font-mono text-gray-medium">{m.manual_min}m → {m.auto_min}m</td>
                    <td className="py-2 text-right">
                      <span className="text-emerald-600 font-medium">{m.reduction_pct}%</span>
                    </td>
                    <td className="py-2 text-right font-mono">{fmtNum(m.hours_saved)}</td>
                    <td className="py-2 text-right font-mono font-semibold text-teal-700">{fmtUSD(m.value_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-start gap-2 text-[11px] text-gray-medium bg-gray-50 rounded-lg p-2">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Benchmarks based on industry data from Gartner, Nucleus Research, HubSpot 2024, McKinsey RPA studies, and Deloitte finance ops.
              Actual results vary by team size and adoption rate. {data.mode === 'demo' && 'Displayed values are simulated based on typical usage for a team of this size.'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
